import React, { Fragment, useState, useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { join, map, pipe } from "ramda";
import {
  useDeletePermission,
  useMediaPermissions,
} from "../mediaAccessServices";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { faPen } from "@fortawesome/pro-solid-svg-icons";
import { isBlank } from "../../util/ext";
import { capitalize } from "../../util/stringFormat";
import { useSnackAlert } from "../../common/use-snackAlert";

import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import ManageMediaAccessDialog from "./ManageMediaAccessDialog";
import PrincipalCard from "../content/edit/PrincipalCard";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { isContent } from "../organize/organizeUtil";
import ConfirmButton from "../../components/ConfirmButton";
import LoadingIndicator from "../../components/LoadingIndicator";
import FontAwesomeIcon from "../../components/FontAwesomeIcon";

const useStyles = makeStyles(theme => ({
  title: {
    width: "auto",
    [theme.breakpoints.up("md")]: {
      marginLeft: "auto",
      marginRight: "auto",
    },
  },

  thumbnail: {
    height: 80,
    width: 80,
  },

  panel: {
    height: "calc(100vh - 200px)",
    padding: "0",
    border: "3px solid white",
    background: "white",
    borderRadius: "20px",
  },
}));

const initialMediaAccess = {
  mediaPermissionsId: null,
  principal: "",
  role: "",
  permissions: null,
};

const formatPermissionList = pipe(map(capitalize), join(", "));

const ManageMediaAccess = ({ media }) => {
  const classes = useStyles();
  const { SnackAlert, showAlert } = useSnackAlert();

  const mediaPermissionsResults = useMediaPermissions(media.id);

  const [mediaAccess, setMediaAccess] = useState(initialMediaAccess);
  const [mediaPermissions, setMediaPermissions] = useState([]);
  useEffect(() => {
    if (mediaPermissionsResults.isSuccess) {
      setMediaPermissions(mediaPermissionsResults.data);
    }
  }, [mediaPermissionsResults.data, mediaPermissionsResults.isSuccess]);

  //-- Manage Dialogs
  const [open, setOpen] = useState({
    access: false,
  });
  const handleOpen = dialog => () =>
    setOpen(prev => ({ ...prev, [dialog]: true }));
  const handleClose = dialog => () => {
    setOpen(prev => ({ ...prev, [dialog]: false }));
    setMediaAccess(initialMediaAccess);
  };

  const handleEditClick = mediaAccess => event => {
    setMediaAccess(mediaAccess);
    handleOpen("access")();
  };

  // Handle Delete
  const deletePermission = useDeletePermission();
  const handlePermissionDelete = mediaPermissionsId =>
    deletePermission.mutateAsync({
      resourceIds: {
        mediaPermissionsId: mediaPermissionsId,
      },
    });

  return (
    <Container className={classes.panel}>
      {mediaPermissionsResults.isLoading ? (
        <PanelLoadingIndicator />
      ) : (
        <Fragment>
          {deletePermission.isPending && <LoadingIndicator transparent />}

          <SnackAlert />

          <ManageMediaAccessDialog
            mediaAccess={mediaAccess}
            mediaId={media.id}
            mediaType={media.mediaType}
            open={open.access}
            onClose={handleClose("access")}
            showAlert={showAlert}
          />

          <Grid
            container
            justifyContent="space-between"
            alignItems="flex-start"
            style={{ padding: 30, paddingTop: 10 }}
          >
            <Grid container item xs sm={6} md>
              <Grid item container direction="column" style={{ width: "auto" }}>
                <Grid item>
                  <Typography variant="h1" style={{ fontWeight: "bolder" }}>
                    Access Control
                  </Typography>
                </Grid>

                <Grid item style={{ paddingBottom: 10 }}>
                  <Typography variant="subtitle2">
                    Add permissions for users or organizations
                  </Typography>
                </Grid>
              </Grid>

              <Grid item container className={classes.title} spacing={3}>
                <Grid item>
                  <Avatar
                    variant="rounded"
                    className={classes.thumbnail}
                    alt={`Thumbnail for ${media?.title ?? media?.name}`}
                    src={media.thumbnailUrl}
                  />
                </Grid>

                <Grid
                  item
                  container
                  direction="column"
                  justifyContent="flex-end"
                  style={{ width: "fit-content" }}
                >
                  <Grid item>
                    <Typography
                      variant="h2"
                      style={{ fontWeight: "bolder", fontSize: "1.8rem" }}
                    >
                      {media?.title ?? media?.displayName ?? media.name}
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Typography variant="subtitle2">{media.id}</Typography>
                  </Grid>

                  {isContent(media) && (
                    <Grid item>
                      <Typography style={{ paddingTop: 5 }}>
                        {capitalize(media.type)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <Button
                onClick={handleOpen("access")}
                startIcon={<FontAwesomeIcon icon={faPlus} />}
              >
                Add Access for User or Organization
              </Button>
            </Grid>
          </Grid>

          {isBlank(mediaPermissions) ? (
            <div style={{ padding: "0 30px" }}>
              <i>
                This media item currently has no permissions. You can set up a
                user or organization with access by clicking the button in the
                upper-right corner.
              </i>
            </div>
          ) : (
            <TableContainer>
              <Table stickyHeader aria-label={`permissions for ${media.id}`}>
                <TableHead>
                  <TableRow>
                    <TableCell>Principal</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {mediaPermissions.map(mp => (
                    <TableRow key={mp.mediaPermissionsId}>
                      <TableCell>
                        <PrincipalCard principal={mp.principal} />
                      </TableCell>

                      <TableCell>{mp.role}</TableCell>

                      <TableCell>
                        {formatPermissionList(mp.permissionList)}
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton
                            onClick={handleEditClick(mp)}
                            size="large"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </IconButton>
                        </Tooltip>

                        <ConfirmButton
                          handleDelete={() =>
                            handlePermissionDelete(mp.mediaPermissionsId)
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Fragment>
      )}
    </Container>
  );
};

export default ManageMediaAccess;
